<template>
  <div
    ref="map"
    :style="{
      height
    }"
  />
</template>

<script>
/* global google */
import mapStyle from '@/components/mapStyle'
import mapStyleDark from '@/components/mapStyleDark'
import TripSteps from '@/models/TripSteps'

export default {
  props: {
    activeStep: {
      type: String,
      default: null
    },
    height: {
      type: String,
      default: null
    },
    steps: {
      type: TripSteps,
      default: null,
      required: true
    }
  },
  data () {
    return {
      initialZoomLevel: 0,
      map: null,
      mapMarkers: {}
    }
  },
  watch: {
    async activeStep (newStep, prevStep) {
      if (!newStep || !this.map.getBounds()) { return }

      const marker = this.mapMarkers[newStep]
      const prevMarker = this.mapMarkers[prevStep]

      if (!marker) { return }

      const isMarkerVisible = this.map.getBounds().contains(marker.getPosition())

      if (!isMarkerVisible) {
        // zoom out
        await this.smoothZoom(this.initialZoomLevel)

        // pan to show both new and prev markers
        if (prevMarker) {
          const bounds = new google.maps.LatLngBounds()
          bounds.extend(marker.getPosition())
          bounds.extend(prevMarker.getPosition())

          await this.timeout(400)
          this.map.panToBounds(bounds)
        }

        await this.timeout(400)
      }

      // pan to new marker
      this.map.panTo(marker.getPosition())

      // zoom in
      await this.smoothZoom(12)
    }
  },
  mounted () {
    this.initMap()
    this.addMarkersToMap()
    if (this.steps.length === 0) {
      this.steps.on('fetch', this.addMarkersToMap)
    }
  },
  beforeDestroy () {
    this.steps.off('fetch', this.addMarkersToMap)
  },
  methods: {
    initMap () {
      // eslint-disable-next-line no-new
      this.map = new google.maps.Map(this.$refs.map, {
        center: { lat: 41.6820147, lng: 12.1483063 },
        zoom: 4,
        disableDefaultUI: true,
        styles: this.$vuetify.theme.isDark ? mapStyleDark : mapStyle
      })
    },
    addMarkersToMap (event) {
      if (this.steps.length) {
        const bounds = new google.maps.LatLngBounds()

        this.steps.each(step => {
          if (!step.$.location || !step.$.lat || !step.$.lng) { return }

          if (!this.mapMarkers[step.id]) {
            this.mapMarkers[step.id] = new google.maps.Marker({
              map: this.map,
              title: step.$.name
            })
          }

          const marker = this.mapMarkers[step.id]
          marker.setPosition({ lat: step.$.lat, lng: step.$.lng })

          const loc = new google.maps.LatLng(marker.position.lat(), marker.position.lng())
          bounds.extend(loc)
        })

        this.map.panToBounds(bounds)
        this.map.fitBounds(bounds)
        const listener = google.maps.event.addListener(this.map, 'idle', () => {
          if (this.map.getZoom() > 14) {
            this.map.setZoom(14)
          }
          // Save zoom level for animations
          if (!this.initialZoomLevel) {
            this.initialZoomLevel = this.map.getZoom()
          }
          google.maps.event.removeListener(listener)
        })
      }
    },
    /**
     * @param {Number} to Google Maps zoom level
     */
    async smoothZoom (to) {
      const from = this.map.getZoom()

      // If the zoom level is the same, do nothing
      if (from === to) { return }

      const delta = Math.sign(to - from)

      let z = from + delta
      while (z !== to) {
        await this.map.setZoom(z)
        await this.timeout(150)

        z += delta
      }

      await this.map.setZoom(z)
    },
    timeout (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    }
  }
}
</script>
